import * as React from 'react';
import { useCallback, useMemo, createContext, useContext, useEffect } from 'react';
import { InjectedTranslateProps, Trans, translate } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { RouteChildrenProps, useHistory, withRouter } from 'react-router-dom';
import * as s from './RolesListComponent.scss';
import StatusAlertFilled from 'wix-ui-icons-common/StatusAlertFilled';
import Add from 'wix-ui-icons-common/Add';
import { State } from '../../redux/configure-store';
import { getAreasWithAssignableRoles, getIsSystemRole } from '../../redux/selectors/roleAreas';
import { showModal } from '../../redux/actions/activeModal.actions';
import { ModalName } from '../../types/ModalNames';
import { getAreas } from '../../redux/selectors/areas';
import { InjectedExperimentsProps, withExperiments } from '@wix/wix-experiments-react';
import {
  reportCreateCustomRoleClick,
  reportDuplicateAndEditClick,
  reportEditCustomRole,
  reportManageRolesClick
} from '../../redux/actions/bi.actions';
import StatusWarningFilledSmall from 'wix-ui-icons-common/StatusWarningFilledSmall';
import { getPageComponentLinkComponent, PageComponentId } from '@wix/business-manager-api';
import { getIsSite } from '../../redux/selectors/config';
import {
  FormField,
  Text,
  Box,
  Col,
  Container,
  Row,
  Checkbox,
  SectionHelper,
  TextButton,
  Tooltip,
} from 'wix-style-react';
import { isBookingsStaffMember } from '../../services/roleService';
import { getManageRolesPermission } from '../../redux/selectors/teamDetails';

const PageComponentLink = getPageComponentLinkComponent();

interface IRolesListComponent extends InjectedTranslateProps, InjectedExperimentsProps, RouteChildrenProps {
  origin: string;
  roleIds?: string[];
  selectedRoles?: string[];
  shouldDisplayError?: boolean;
  emails?: string[];

  updateSelectedRoles(selectedRoles: string[]): void;
}

// This area should be restructured in the future
// maybe we should add a contains attribute to the role config file and remove some of the helpers
const getIsRoleSelected = (isSystemRole, roleId: string, selectedRoles: string[], isSite: boolean, roleScopes: string[], isSaveAction?: boolean): boolean => {
  if (isSaveAction) {
    return selectedRoles.includes(roleId) && !getIsRoleDisableTEMP(isSystemRole, roleId, selectedRoles, isSite, roleScopes);
  } else {
    return selectedRoles.includes(roleId) || (getIsRoleDisableTEMP(isSystemRole, roleId, selectedRoles, isSite, roleScopes) && !isBookingsStaffMember(roleId) && !shouldDisableRoleWithAccountScopes(isSite, roleScopes));
  }
};

const getIsRoleDisableTEMP = (isSystemRole, roleId: string, selectedRoles: string[], isSite: boolean, roleScopes: string[]): boolean => {
  if ( selectedRoles.includes('6642345101181663232') ) { //Co-Owner
    return roleId !== '6642345101181663232';
  }
  switch (roleId) {
    case '6600344267119876119':  //Back Office Manager
      return selectedRoles.some(selectedRole => selectedRole === '6600343328279451657' || selectedRole === '6600343568810203151' || selectedRole === '6643122506063252487' || selectedRole === '6600343568810203151');
    case '6600343671872641041':  //Website Manager
      return selectedRoles.includes('6600343328279451657');
    case '6600342285084743683':  //Limited Website Manager
      return selectedRoles.includes('6600343568810203151') || selectedRoles.includes('6600343671872641041') || selectedRoles.includes('6600343328279451657');
    case '6600343568810203151': //Limited Admin
      return selectedRoles.includes('6600343328279451657');
    case '6643122105421848577':  //Account Website Manager
      return selectedRoles.includes('6643122506063252487');
    case '6643122648174660618':  //Account Limited Admin
      return selectedRoles.includes('6643122506063252487');
    case '6653312585132492834': //Domains Manager
      return selectedRoles.includes('6642345601440495619');
    case '6600344420111308827': //Blog Editor
      return selectedRoles.some(isSystemRole);
    case '6600343770271012883': //Blog Writer
      return selectedRoles.includes('6600344420111308827') || selectedRoles.some(isSystemRole);
    case '6600342952117491717': //Bookings Staff Member
      return !selectedRoles.some(isBookingsStaffMember);
    default:
      return shouldDisableRoleWithAccountScopes(isSite, roleScopes);
  }
};
//always false, will be removed in the future
const shouldDisableRoleWithAccountScopes = (isSite: boolean, roleScopes: string[]): boolean =>
  false; //isSite && isRoleContainAccountScope(roleScopes);

const getDisabledRoleTooltipText = (isSite: boolean, roleScopes: string[]): string =>
  shouldDisableRoleWithAccountScopes(isSite, roleScopes) ? 'rolesListComponent.disabledRole.accountScopes.tooltipText' : 'rolesListComponent.disabledRole.tooltipText';

const getDisabledRoleText = (t, roleId: string, isSite: boolean) => {
  if (isBookingsStaffMember(roleId)) {
    // Bookings Staff Member is restrictFromLevel account so isSite should always be true
    // ￿However, if we're in account level and a user/an invite has Bookings Staff Member among his/her roles this will throw an error
    // So, I added isSite to disable PageComponentLink render in account level.
    return (
      <Box verticalAlign="middle" marginTop="2px">
        <StatusWarningFilledSmall style={{ fill: '#fdb10c', marginRight: '6px' }}/>
        <Text size="small" weight="thin" secondary> {t('role.bookingStaffMember.disableText')} </Text>
        { isSite ?
          (<Tooltip
          maxWidth="220px"
          textAlign="center"
          content={t('role.bookingStaffMember.tooltipText')}>
            <PageComponentLink pageComponentId={PageComponentId.BookingsStaffList} className={s.disabledTooltip}>
              <TextButton size="small" weight="thin" secondary> {t('role.bookingStaffMember.buttonText')} </TextButton>
            </PageComponentLink>
          </Tooltip>) :
          (<Text size="small" weight="thin" secondary className={s.disabledTooltip}> {t('role.bookingStaffMember.buttonText')} </Text>)
        }
      </Box>
    );
  } else {
    return null;
  }
};

const RolesListContext = createContext<any>(undefined);

const RolesListComponent = ({ t, experiments, selectedRoles = [], updateSelectedRoles, shouldDisplayError = false,  origin, roleIds = [], emails = []  }: IRolesListComponent) => {

  const areas = useSelector((state: State) => getAreasWithAssignableRoles(state, experiments, selectedRoles));
  const isSystemRole = useSelector(getIsSystemRole);

  const experimentCustomRoles = useMemo(() => experiments.enabled('specs.wos.CustomRoles'), [experiments]);
  const shouldDisplayPredefinedRolesViewPermissions = useMemo(() => experimentCustomRoles && experiments?.enabled('specs.ident.PredefinedRolesScopes'), [experimentCustomRoles, experiments]);

  return (<Container fluid className={s.rolesListComponentContainer}>
      <RolesListHeader t={t} shouldSelectARole={shouldDisplayError} origin={origin} experimentCustomRoles={experimentCustomRoles}/>
      <RolesListContext.Provider value={{
        t,
        updateSelectedRoles,
        selectedRoles,
        origin,
        roleIds,
        experimentCustomRoles,
        shouldDisplayPredefinedRolesViewPermissions,
        emails,
        isSystemRole,
      }}>
        <RolesListBody areas={areas}/>
      </RolesListContext.Provider>
    </Container>);
};

const RolesListHeader = ({ t, shouldSelectARole, origin, experimentCustomRoles }) => {
  const dispatch = useDispatch();
  const history = useHistory();

  const manageRolesPermission = useSelector(getManageRolesPermission);

  const onManageRolesClick = useCallback(() => {
    dispatch(reportManageRolesClick(origin));
    history.push('/roles', { origin });
  }, [dispatch, origin, history]);

  return (<>
    <Row className={s.roleListFirstRow}>
      <Col>
        <Box direction="vertical" className={s.titleContainer}>
          <FormField label={t('rolesListComponent.roles')} required/>
          <div>
            { experimentCustomRoles && manageRolesPermission ?
              <Trans i18nKey="rolesListComponent.selectTeammate.long">
                <Text size="small" secondary light>0</Text>
                <TextButton size="small" onClick={onManageRolesClick}>1</TextButton>
              </Trans> :
              <Text size="small" secondary light>{t('rolesListComponent.selectTeammate.short')}</Text> }
          </div>
        </Box>
      </Col>
    </Row>
    {shouldSelectARole && <Row className={s.roleListErrorRow}>
      <Col>
        <Box direction="vertical">
          <SectionHelper dataHook="rolesErrorSection" appearance="danger" showCloseButton={false}>
            <Box verticalAlign="middle" height="18px">
              <StatusAlertFilled style={{ fill: '#ee5951' }}/>
              <Text size="small" weight="thin" className={s.shouldSelectARole}>{t('rolesListComponent.pleaseSelectARole')}</Text>
            </Box>
          </SectionHelper>
        </Box>
      </Col>
    </Row>}
  </>);
};

const RolesListBody = ({ areas }) => {
  return areas.map(area => (
    <Row key={`${area}-${area.roles.map(role => role.id).join(',')}`} className={s.roleBodyRow}>
      <Col>
        <Box height="36px" verticalAlign="middle">
          <Text size="small" secondary light>{area.title}</Text>
        </Box>
      </Col>
      <RolesGroup roles={area.roles} />
    </Row>
  ));
};

const RolesGroup = ({ roles }) => {
  return roles.map((role, rowIndex) =>
    <RoleRow key={rowIndex} role={role} />);
};

const RoleRow = ({ role }) => {

  const { t, updateSelectedRoles, selectedRoles, origin, roleIds, experimentCustomRoles, shouldDisplayPredefinedRolesViewPermissions, emails, isSystemRole } = useContext(RolesListContext);
  const dispatch = useDispatch();
  const history = useHistory();

  const areas = useSelector(getAreas);
  const manageRolesPermission = useSelector(getManageRolesPermission);
  const isSite = useSelector(getIsSite);

  const checked = getIsRoleSelected(isSystemRole, role.id, selectedRoles, isSite, role.scopes);
  const disabled = getIsRoleDisableTEMP(isSystemRole, role.id, selectedRoles, isSite, role.scopes);

  useEffect(() => {
    if (checked && disabled && shouldDisableRoleWithAccountScopes(isSite, role.scopes)) {
      updateSelectedRoles(selectedRoles.filter((selectedRole) => selectedRole !== role.id));
    }
  }, [checked, disabled, isSite, role.scopes, updateSelectedRoles, selectedRoles, role.id]);

  const permissions = useMemo(() => {
    return areas.map(area => {
      const selectedScopes = area.scopes.filter(scope => role.scopes.includes(scope.id));
      if (selectedScopes.length > 0) {
        return {
          title: area.title,
          scopes: selectedScopes.map(selectedScope => ({ title: selectedScope.title, description: selectedScope.description })),
        };
      } else {
        return null;
      }
    }).filter(area => area);
  }, [role, areas]);

  const onDuplicateAndEditAction = useCallback(() => {
    dispatch(reportDuplicateAndEditClick(origin, role.id));
    history.push(`/role/new?duplicateId=${role.id}`, { origin, roleIds, emails });
  }, [role.id, origin, roleIds, emails, history, dispatch]);

  const onEditAction = useCallback(() => {
    dispatch(reportEditCustomRole(role.id));
    history.push(`/role/${role.id}/edit`, { origin, roleIds, emails });
  }, [role.id, origin, roleIds, emails, history, dispatch]);

  const onViewRolePermissionsAction = useCallback((e) => {
    let viewRoleModalParams = { roleTitle: role.title, permissions };
    if (manageRolesPermission) {
      viewRoleModalParams['onDuplicateAndEditAction'] = onDuplicateAndEditAction;
      if (role.isCustom) {
        viewRoleModalParams['onEditAction'] = onEditAction;
      }
    }
    dispatch(showModal(ModalName.ViewRolePermissions, viewRoleModalParams));
    e.stopPropagation();
  }, [role.title, role.isCustom, permissions, onDuplicateAndEditAction, onEditAction, manageRolesPermission, dispatch]);

  const handleRoleClick = useCallback(({ id }) => {
    let newSelectedRoles;
    if (selectedRoles.includes(id)) {
      newSelectedRoles = selectedRoles.filter((selectedRole) => selectedRole !== id);
    } else {
      newSelectedRoles = selectedRoles.concat([id]);
      newSelectedRoles = newSelectedRoles.filter(selectedRole => getIsRoleSelected(isSystemRole, selectedRole, newSelectedRoles, isSite, role.scopes, true));
    }
    updateSelectedRoles(newSelectedRoles);
  }, [selectedRoles, isSystemRole, isSite]);

  const onRoleChange = useCallback(() => {
    handleRoleClick(role);
  }, [handleRoleClick]);

  const shouldDisplayViewRolePermissions = useMemo(() => role.isCustom ? experimentCustomRoles : shouldDisplayPredefinedRolesViewPermissions, [role.isCustom, experimentCustomRoles, shouldDisplayPredefinedRolesViewPermissions]);

  return (
    <Col className={s.roleRow}>
      <Tooltip disabled={!disabled || isBookingsStaffMember(role.id)} placement="top-start" textAlign="center" content={t(getDisabledRoleTooltipText(isSite, role.scopes))} maxWidth={220}>
        <Box cursor={ disabled && 'default' }>
          <Checkbox dataHook={`rolesListComponent-checkbox-${role.id}`} vAlign="top" onChange={onRoleChange} checked={checked} disabled={disabled}/>
          <Box
            marginLeft="12px"
            direction="vertical" width="100%">
            <Text size="medium" weight="normal" onClick={!disabled ? onRoleChange : undefined} skin={ disabled ? 'disabled' : 'standard' }>{role.title}</Text>
            <Text size="small" secondary={!disabled} onClick={!disabled ? onRoleChange : undefined} skin={ disabled ? 'disabled' : 'standard' } className={s.roleDescription}>
              {role.description}
              {shouldDisplayViewRolePermissions && <TextButton className={(!!role.description) ? s.viewRolePermissionsButton : undefined} onClick={onViewRolePermissionsAction} size="small" underline="onHover">{t('role.viewRolePermissions')}</TextButton>}
            </Text>
            { disabled && getDisabledRoleText(t, role.id, isSite) }
          </Box>
        </Box>
      </Tooltip>
    </Col>);
};

export const RolesListFooter = ({ t, origin, roleIds = [], emails }: { t: any; origin: string; roleIds: string[]; emails?: string[] } ) => {

  const dispatch = useDispatch();
  const history = useHistory();

  const manageRolesPermission = useSelector(getManageRolesPermission);

  const onCreateRoleClick = useCallback(() => {
    dispatch(reportCreateCustomRoleClick(origin));
    history.push(`/role/new`, { roleIds, origin, emails });
  }, [origin, roleIds, history, emails, dispatch]);

  return manageRolesPermission ?
      (<Box className={s.footerRow} verticalAlign="middle" dataHook="createNewRoleFooter">
        <Add fill="#3899ec" onClick={onCreateRoleClick}/>
        <Box marginLeft="medium" direction="vertical" width="100%">
          <TextButton size="medium" weight="normal" onClick={onCreateRoleClick}>{t('rolesListComponent.customizePermission.title')}</TextButton>
          <TextButton size="small" weight="thin" onClick={onCreateRoleClick}>
            {t('rolesListComponent.customizePermission.body')}
          </TextButton>
        </Box>
      </Box>) : null;
};

export default withRouter(withExperiments(translate()(RolesListComponent)));
